<script lang="ts">
  import { onMount, tick } from "svelte";
  import { api, ApiError, type ChatMessage } from "../lib/api";
  import {
    buildRequestMessages,
    loadSettings,
    resolveMode,
    streamChat,
    streamChatViaServer,
    type AiMode,
    type AiSettings,
    type ServerProviderConfig,
  } from "../lib/ai";
  import { TOOL_DEFS, toolResult, type ToolCall } from "../lib/tools";
  import { highlight } from "../lib/highlight";
  import AiSettingsDialog from "./AiSettings.svelte";

  interface Props {
    slug: string;
    title: string;
    files: Array<{ path: string; content: string }>;
    activePath: string;
    /** Исполняет инструмент модели; возвращает результат строкой. */
    executeTool: (name: string, args: Record<string, unknown>) => Promise<string>;
    onClose: () => void;
  }

  let { slug, title, files, activePath, executeTool, onClose }: Props = $props();

  let messages = $state<ChatMessage[]>([]);
  let hasMore = $state(false);
  let loading = $state(false);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  let warn = $state(false);
  let totalBytes = $state(0);

  let input = $state("");
  let streaming = $state(false);
  let streamText = $state("");
  let streamReasoning = $state("");
  let controller: AbortController | null = null;

  let copied = $state<string | null>(null);
  let showThinking = $state(false);

  let settings = $state<AiSettings>(loadSettings());
  let serverProvider = $state<ServerProviderConfig | null>(null);
  let showSettings = $state(false);
  let listEl: HTMLDivElement;

  // Свой провайдер из localStorage или серверный (ключ живёт на сервере).
  const mode: AiMode = $derived(resolveMode(settings, serverProvider));
  const configured = $derived(mode !== "none");
  const modelLabel = $derived(
    mode === "direct" ? settings.model : mode === "server" ? serverProvider?.model ?? "" : "",
  );

  async function scrollToBottom() {
    await tick();
    if (listEl) listEl.scrollTop = listEl.scrollHeight;
  }

  async function loadInitial() {
    loading = true;
    error = null;
    try {
      const page = await api.listChat(slug, { limit: 50 });
      messages = page.messages;
      hasMore = page.hasMore;
      totalBytes = page.totalBytes;
      warn = page.warn;
      await scrollToBottom();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  async function loadOlder() {
    if (!hasMore || loadingMore || messages.length === 0) return;
    loadingMore = true;
    const before = messages[0]!.seq;
    try {
      const page = await api.listChat(slug, { limit: 50, before });
      messages = [...page.messages, ...messages];
      hasMore = page.hasMore;
      totalBytes = page.totalBytes;
      warn = page.warn;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loadingMore = false;
    }
  }

  function onScroll() {
    if (listEl && listEl.scrollTop < 40) void loadOlder();
  }

  const MAX_TOOL_STEPS = 100;

  async function persistAssistant(
    content: string,
    meta: Record<string, unknown>,
  ): Promise<ChatMessage | null> {
    const res = await api.appendChat(slug, [{ role: "assistant", content, meta }]);
    totalBytes = res.totalBytes;
    warn = res.warn;
    return res.messages[0] ?? null;
  }

  async function persistTool(
    call: ToolCall,
    result: string,
  ): Promise<ChatMessage | null> {
    const res = await api.appendChat(slug, [
      {
        role: "tool",
        content: toolResult(result),
        meta: { toolCallId: call.id, name: call.function.name },
      },
    ]);
    totalBytes = res.totalBytes;
    warn = res.warn;
    return res.messages[0] ?? null;
  }

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;
    if (!configured) {
      showSettings = true;
      return;
    }
    input = "";
    error = null;

    const pending: ChatMessage = {
      id: `local-${Date.now()}`,
      seq: Number.MAX_SAFE_INTEGER,
      role: "user",
      content: text,
      meta: null,
      createdAt: Date.now(),
    };
    messages = [...messages, pending];
    await scrollToBottom();

    // Сначала фиксируем сообщение пользователя, чтобы оно не потерялось.
    try {
      const res = await api.appendChat(slug, [{ role: "user", content: text }]);
      totalBytes = res.totalBytes;
      warn = res.warn;
      const saved = res.messages[0];
      if (saved) messages = messages.map((m) => (m.id === pending.id ? saved : m));
    } catch (e) {
      error = chatErrorText(e);
      messages = messages.filter((m) => m.id !== pending.id);
      return;
    }

    streaming = true;
    controller = new AbortController();

    try {
      for (let step = 0; step < MAX_TOOL_STEPS; step++) {
        streamText = "";
        streamReasoning = "";

        const requestMessages = buildRequestMessages({
          settings,
          title,
          files,
          activePath,
          history: messages,
        });

        const onDelta = (d: string) => {
          streamText += d;
          void scrollToBottom();
        };
        const onReasoning = (d: string) => {
          streamReasoning += d;
          void scrollToBottom();
        };

        const result =
          mode === "server"
            ? await streamChatViaServer({
                messages: requestMessages,
                temperature: settings.temperature,
                maxTokens: settings.autoMaxTokens ? undefined : settings.maxTokens,
                tools: settings.useTools ? TOOL_DEFS : undefined,
                signal: controller.signal,
                onDelta,
                onReasoning,
              })
            : await streamChat({
                settings,
                messages: requestMessages,
                signal: controller.signal,
                onDelta,
                onReasoning,
              });

        const hasContent = result.text.trim() !== "" || result.reasoning.trim() !== "";
        const calls = result.toolCalls;

        if (hasContent || calls.length > 0) {
          const saved = await persistAssistant(result.text, {
            model: modelLabel,
            reasoning: result.reasoning || undefined,
            finishReason: result.finishReason ?? undefined,
            maxTokens: settings.autoMaxTokens ? undefined : settings.maxTokens,
            toolCalls: calls.length > 0 ? calls : undefined,
          });
          if (saved) messages = [...messages, saved];
          streamText = "";
          streamReasoning = "";
        }

        if (calls.length === 0) break;

        // Исполняем вызовы по очереди и возвращаем результаты модели.
        for (const call of calls) {
          let args: Record<string, unknown> = {};
          try {
            const parsed = JSON.parse(call.function.arguments || "{}");
            if (parsed && typeof parsed === "object") args = parsed as Record<string, unknown>;
          } catch {
            /* модель прислала битый JSON — отдадим ошибку инструменту */
          }

          let result: string;
          try {
            result = await executeTool(call.function.name, args);
          } catch (e) {
            result = `error: ${e instanceof Error ? e.message : String(e)}`;
          }

          const saved = await persistTool(call, result);
          if (saved) messages = [...messages, saved];
          await scrollToBottom();
        }

        if (step === MAX_TOOL_STEPS - 1) {
          error = `остановлено: больше ${MAX_TOOL_STEPS} шагов инструментов`;
        }
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        if (streamText.trim() !== "" || streamReasoning.trim() !== "") {
          try {
            const saved = await persistAssistant(streamText, {
              model: modelLabel,
              reasoning: streamReasoning || undefined,
              partial: true,
            });
            if (saved) messages = [...messages, saved];
          } catch {
            /* ignore */
          }
        }
      } else {
        error = chatErrorText(e);
      }
    } finally {
      streaming = false;
      streamText = "";
      streamReasoning = "";
      controller = null;
      await scrollToBottom();
    }
  }

  function stop() {
    controller?.abort();
  }

  function chatErrorText(e: unknown): string {
    if (e instanceof ApiError && (e.message === "chat_full" || e.status === 409)) {
      return "история чата заполнена — очистите её, чтобы продолжить";
    }
    const msg = e instanceof Error ? e.message : String(e);
    // Типичный случай для прямого режима: браузер не пустил к приватному адресу.
    if (mode === "direct" && /failed to fetch|networkerror|load failed/i.test(msg)) {
      return `${msg} — браузер заблокировал запрос. Если провайдер в локальной сети, разрешите сайту доступ к локальной сети (Chrome: значок замка → настройки сайта → local network access).`;
    }
    return msg;
  }

  async function clearChat() {
    if (!confirm("очистить историю чата? появится новый тред, старый удалится при превышении лимита.")) return;
    try {
      await api.clearChat(slug);
      messages = [];
      hasMore = false;
      totalBytes = 0;
      warn = false;
    } catch (e) {
      error = chatErrorText(e);
    }
  }

  interface MsgMeta {
    model?: string;
    reasoning?: string;
    finishReason?: string;
    maxTokens?: number;
    partial?: boolean;
    toolCalls?: ToolCall[];
    toolCallId?: string;
    name?: string;
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      copied = text;
      setTimeout(() => {
        if (copied === text) copied = null;
      }, 1500);
    } catch (e) {
      error = `не удалось скопировать: ${e instanceof Error ? e.message : String(e)}`;
    }
  }

  /** Краткое представление аргументов вызова для чипа в ленте. */
  function shortArgs(raw: string): string {
    const oneLine = raw.replace(/\s+/g, " ").trim();
    return oneLine.length > 90 ? `${oneLine.slice(0, 90)}…` : oneLine;
  }

  function metaOf(m: ChatMessage): MsgMeta {
    return (m.meta && typeof m.meta === "object" ? m.meta : {}) as MsgMeta;
  }

  /** Ответ обрезан по лимиту токенов (обычно из-за reasoning). */
  function truncated(m: ChatMessage): boolean {
    return metaOf(m).finishReason === "length";
  }

  /** Разбивает markdown-подобный текст на обычные фрагменты и код. */
  interface Segment {
    type: "text" | "code";
    lang: string;
    content: string;
  }

  function segments(text: string): Segment[] {
    const out: Segment[] = [];
    const re = /```([a-zA-Z0-9_-]*)\n([\s\S]*?)```/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push({ type: "text", lang: "", content: text.slice(last, m.index) });
      out.push({ type: "code", lang: m[1] ?? "", content: (m[2] ?? "").replace(/\n$/, "") });
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ type: "text", lang: "", content: text.slice(last) });
    return out.filter((s) => s.content.trim() !== "");
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  function fmtBytes(n: number): string {
    if (n < 1024) return `${n} b`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kb`;
    return `${(n / (1024 * 1024)).toFixed(2)} mb`;
  }

  async function loadServerProvider() {
    try {
      serverProvider = await api.aiConfig();
    } catch {
      serverProvider = null;
    }
  }

  onMount(() => {
    void loadServerProvider();
    void loadInitial();
  });
</script>

<div class="chat">
  <div class="chat-header">
    <span class="ai-label">ai</span>
    <span class="spacer"></span>
    {#if modelLabel}
      <span class="muted model" title="модель">{modelLabel}</span>
    {/if}
    {#if totalBytes > 0}
      <span class="muted" class:warn>{fmtBytes(totalBytes)}</span>
    {/if}
    <button class:active={showThinking} onclick={() => (showThinking = !showThinking)} title="развернуть все thinking-блоки">
      think
    </button>
    <button onclick={() => (showSettings = true)} title="настройки провайдера">settings</button>
    <button onclick={clearChat} title="очистить историю">clear</button>
    <button onclick={onClose} title="закрыть панель">×</button>
  </div>

  <div class="chat-list" bind:this={listEl} onscroll={onScroll}>
    {#if loading}
      <div class="chat-empty muted">loading…</div>
    {:else if messages.length === 0}
      <div class="chat-empty muted">
        {#if configured}
          спросите что-нибудь про код в этом пасте.
        {:else}
          провайдер не настроен. нажмите <button class="inline" onclick={() => (showSettings = true)}>settings</button>.
        {/if}
      </div>
    {:else}
      {#if hasMore}
        <div class="chat-more">
          <button onclick={loadOlder} disabled={loadingMore}>{loadingMore ? "loading…" : "load older"}</button>
        </div>
      {/if}
      {#each messages as m (m.id)}
        <div class="msg" class:user={m.role === "user"} class:tool-msg={m.role === "tool"}>
          <div class="msg-role muted">
            {m.role}{#if m.role === "tool" && metaOf(m).name} · {metaOf(m).name}{/if}
          </div>
          {#if m.role === "tool"}
            <pre class="tool-result">{m.content}</pre>
          {:else if m.role === "user"}
            <div class="msg-text user-text">{m.content}</div>
          {:else}
            {#if metaOf(m).reasoning}
              <details class="thinking" open={showThinking}>
                <summary>thinking · {metaOf(m).reasoning!.length} симв.</summary>
                <div class="thinking-body">{metaOf(m).reasoning}</div>
              </details>
            {/if}

            {#each segments(m.content) as seg}
              {#if seg.type === "text"}
                <div class="msg-text">{seg.content}</div>
              {:else}
                <div class="code">
                  <div class="code-head">
                    <span class="muted">{seg.lang || "code"}</span>
                    <span class="spacer"></span>
                    <button onclick={() => copy(seg.content)}>
                      {copied === seg.content ? "copied" : "copy"}
                    </button>
                  </div>
                  <pre><code>{@html highlight(seg.content, seg.lang)}</code></pre>
                </div>
              {/if}
            {/each}

            {#if metaOf(m).toolCalls?.length}
              <div class="tool-calls">
                {#each metaOf(m).toolCalls ?? [] as call (call.id)}
                  <div class="tool-call">
                    <span class="tool-name">{call.function.name}</span>
                    <span class="muted">{shortArgs(call.function.arguments)}</span>
                  </div>
                {/each}
              </div>
            {/if}

            {#if truncated(m)}
              <div class="truncated">
                ответ обрезан по лимиту
                {metaOf(m).maxTokens ? `${metaOf(m).maxTokens} токенов` : "провайдера"} —
                рассуждения израсходовали бюджет.
                {#if !m.content.trim()}текста не осталось.{/if}
                {#if metaOf(m).maxTokens}увеличьте max tokens или включите auto в настройках.{/if}
              </div>
            {/if}
          {/if}
        </div>
      {/each}
    {/if}

    {#if streaming}
      <div class="msg">
        <div class="msg-role muted">assistant</div>
        {#if streamReasoning}
          <details class="thinking" open>
            <summary>thinking · {streamReasoning.length} симв.</summary>
            <div class="thinking-body">{streamReasoning}</div>
          </details>
        {/if}
        {#if streamText === ""}
          {#if !streamReasoning}
            <div class="msg-text muted">thinking…</div>
          {/if}
        {:else}
          {#each segments(streamText) as seg}
            {#if seg.type === "text"}
              <div class="msg-text">{seg.content}</div>
            {:else}
              <pre class="code-stream"><code>{seg.content}</code></pre>
            {/if}
          {/each}
        {/if}
      </div>
    {/if}
  </div>

  {#if warn}
    <div class="chat-warn">история чата приближается к лимиту</div>
  {/if}
  {#if error}
    <div class="chat-error">{error}</div>
  {/if}

  <div class="chat-input">
    <textarea
      bind:value={input}
      onkeydown={onKeydown}
      placeholder={configured ? (mode === "server" ? `спросить ${modelLabel}…` : "ask about the code…") : "настройте провайдера в settings"}
      rows="3"
      disabled={streaming}
    ></textarea>
    {#if streaming}
      <button onclick={stop}>stop</button>
    {:else}
      <button class="primary" onclick={send} disabled={!input.trim()}>send</button>
    {/if}
  </div>
</div>

{#if showSettings}
  <AiSettingsDialog
    onClose={() => (showSettings = false)}
    onSaved={() => (settings = loadSettings())}
  />
{/if}

<style>
  .chat {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: var(--bg-elev);
  }
  .chat-header {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px 3px 10px;
    border-bottom: 1px solid var(--border);
    min-height: 26px;
    flex-shrink: 0;
  }
  .chat-header button { padding: 1px 6px; }
  .ai-label {
    background-image: linear-gradient(90deg, #4ec9b0, #7aa2f7 55%, #bb9af7);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
  }
  .spacer { flex: 1; }
  .model { max-width: 45%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .warn { color: var(--danger); }

  .chat-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding: 6px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .chat-empty { padding: 16px 6px; text-align: center; }
  .chat-more { text-align: center; }
  .chat-more button { padding: 1px 8px; }
  .inline {
    background: transparent;
    border: 0;
    padding: 0;
    color: var(--accent);
    text-decoration: underline;
  }

  .msg { display: flex; flex-direction: column; gap: 3px; }
  .msg-role { opacity: 0.7; }
  .msg-text { white-space: pre-wrap; word-break: break-word; }
  .user .msg-text { color: var(--fg); }
  .user-text {
    background: var(--bg);
    border-left: 2px solid var(--accent);
    padding: 3px 8px;
  }

  .tool-calls { display: flex; flex-direction: column; gap: 3px; }
  .tool-call {
    display: flex;
    gap: 8px;
    align-items: baseline;
    border-left: 2px solid var(--accent);
    padding: 2px 8px;
    background: var(--bg);
    overflow: hidden;
  }
  .tool-name { color: var(--accent); white-space: nowrap; }
  .tool-result {
    margin: 0;
    padding: 4px 8px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 3px;
    max-height: 180px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: var(--mono);
    font-size: var(--size);
  }
  .tool-msg .msg-role { color: var(--fg-dim); }

  :global(.hljs-keyword),
  :global(.hljs-selector-tag),
  :global(.hljs-built_in) { color: #c586c0; }
  :global(.hljs-string),
  :global(.hljs-attr) { color: #ce9178; }
  :global(.hljs-number),
  :global(.hljs-literal) { color: #b5cea8; }
  :global(.hljs-comment) { color: #6a9955; font-style: italic; }
  :global(.hljs-title),
  :global(.hljs-function) { color: #dcdcaa; }
  :global(.hljs-variable),
  :global(.hljs-params) { color: #9cdcfe; }
  :global(.hljs-tag),
  :global(.hljs-name) { color: #569cd6; }
  :global(.hljs-property) { color: #9cdcfe; }

  .thinking {
    border: 1px dashed var(--border);
    border-radius: 3px;
    padding: 2px 8px;
    color: var(--fg-dim);
  }
  .thinking summary { cursor: pointer; }
  .thinking-body {
    margin-top: 4px;
    white-space: pre-wrap;
    word-break: break-word;
    border-top: 1px solid var(--border);
    padding-top: 4px;
  }
  .truncated {
    color: var(--danger);
    border-left: 2px solid var(--danger);
    padding: 3px 8px;
  }

  .code {
    border: 1px solid var(--border);
    border-radius: 3px;
    background: var(--bg);
    overflow: hidden;
  }
  .code-head {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px 2px 8px;
    border-bottom: 1px solid var(--border);
  }
  .code-head button { padding: 0 5px; }
  .code pre, .code-stream {
    margin: 0;
    padding: 6px 8px;
    overflow-x: auto;
    font-family: var(--mono);
    font-size: var(--size);
    white-space: pre;
  }
  .code-stream {
    border: 1px solid var(--border);
    border-radius: 3px;
    background: var(--bg);
  }

  .chat-warn, .chat-error {
    padding: 3px 10px;
    border-top: 1px solid var(--border);
    flex-shrink: 0;
  }
  .chat-warn { color: var(--danger); }
  .chat-error { color: var(--danger); word-break: break-word; }

  .chat-input {
    display: flex;
    gap: 6px;
    padding: 6px;
    border-top: 1px solid var(--border);
    align-items: flex-end;
    flex-shrink: 0;
  }
  .chat-input textarea {
    flex: 1;
    resize: none;
    background: var(--bg);
    color: var(--fg);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 4px 8px;
    font-family: var(--font);
    font-size: var(--size);
    outline: none;
  }
  .chat-input textarea:focus { border-color: var(--accent); }
</style>